import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { box } from 'ngrx-forms';
import * as R from 'ramda';
import { identity } from 'ramda';

import { valueAccessorFactory } from '../../helpers';
import { hasNoValue, hasValue } from '../../util';
import { BaseInputDirective } from '../base-input.directive';
import { formControlFactory } from '../form-control.provider';
import { Color } from './color';

type ColorType = Color | Color[];

@Component({
  selector: 'pcs-color-picker',
  template: `
    <div class="d-flex justify-content-between">
      <pcs-color-item *ngFor="let color of colors"
                      [color]="color"
                      [disabled]="disabled"
                      [selected]="isColorSelected(color)"
                      (colorSelected)="onColorSelect($event)">
      </pcs-color-item>
    </div>
  `,
  providers: [
    valueAccessorFactory(ColorPickerComponent),
    formControlFactory(ColorPickerComponent)
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColorPickerComponent extends BaseInputDirective implements OnInit, OnChanges {
  @Input() value: ColorType;
  @Input() colors: Color[] = [];
  @Input() multiSelection = false;

  @Output() selectionChanged = new EventEmitter();

  protected propagateChange = identity;
  protected propagateTouched = identity;

  ngOnInit() {
    if (hasNoValue(this.value)) {
      this.value = this.emptyValue();
    }
  }

  ngOnChanges({disabled}: SimpleChanges) {
    if (disabled && !disabled.isFirstChange()) {
      this.value = this.emptyValue();
      this.colorSelectionChanged();
    }
  }

  writeValue(value: ColorType): void {
    this.value = hasValue(value)
      ? value
      : this.emptyValue();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  isColorSelected(color) {
    return this.multiSelection
      ? R.includes(color, this.value as Color[])
      : R.equals(color, this.value);
  }

  onColorSelect(color) {
    if (this.isColorSelected(color)) {
      this.value = this.multiSelection ? R.filter(c => !R.equals(c, color), this.value as Color[]) : this.value;
    } else {
      this.value = this.multiSelection ? [...this.value as Color[], color] : color;
    }
    this.colorSelectionChanged();
  }

  private colorSelectionChanged() {
    this.propagateChange(box(this.value));
    this.selectionChanged.emit(this.value);
  }

  private emptyValue() {
    return this.multiSelection ? [] : null;
  }
}
